import { gql } from '@apollo/client'

/**
 * Directory CLAIM + REMOVAL documents (plan §3.1/§3.2/§3.6).
 *
 * `DirectoryClaimSelf` is a deliberately narrow projection — it carries no
 * admin notes, no audit trail and no document URLs. That is a server-side
 * type boundary, not a selection choice, so there is nothing here to
 * accidentally over-select.
 */
const CLAIM_SELF_FIELDS = `
  id
  locationId
  status
  strength
  documentCount
  submittedAt
  decidedAt
  rejectionReason
  evidence {
    kind
    detail
    verifiedAt
  }
  options {
    domain
    domainMatches
    emailMasked
    phoneMasked
  }
`

export const MY_DIRECTORY_CLAIM = gql`
  query MyDirectoryClaim($locationId: String!) {
    myDirectoryClaim(locationId: $locationId) {
      ${CLAIM_SELF_FIELDS}
    }
  }
`

export const START_DIRECTORY_CLAIM = gql`
  mutation StartDirectoryClaim($input: StartDirectoryClaimInput!) {
    startDirectoryClaim(input: $input) {
      ${CLAIM_SELF_FIELDS}
    }
  }
`

/**
 * The login-wall flip (2026-08-28, spec docs/plans/2026-08-28-002): a guest
 * opens the DRAFT with name + WhatsApp first; the signed token lets the
 * account they sign in with adopt it via LINK_DIRECTORY_CLAIM.
 */
export const START_DIRECTORY_CLAIM_GUEST = gql`
  mutation StartDirectoryClaimGuest($input: StartDirectoryClaimInput!) {
    startDirectoryClaimGuest(input: $input) {
      guestToken
      claim {
        ${CLAIM_SELF_FIELDS}
      }
    }
  }
`

export const LINK_DIRECTORY_CLAIM = gql`
  mutation LinkDirectoryClaim($guestToken: String!) {
    linkDirectoryClaim(guestToken: $guestToken) {
      ${CLAIM_SELF_FIELDS}
    }
  }
`

export const SEND_DIRECTORY_CLAIM_CODE = gql`
  mutation SendDirectoryClaimCode(
    $claimId: String!
    $channel: DirectoryClaimCodeChannel!
  ) {
    sendDirectoryClaimCode(claimId: $claimId, channel: $channel) {
      sentTo
      expiresAt
    }
  }
`

export const VERIFY_DIRECTORY_CLAIM_CODE = gql`
  mutation VerifyDirectoryClaimCode($claimId: String!, $code: String!) {
    verifyDirectoryClaimCode(claimId: $claimId, code: $code) {
      ${CLAIM_SELF_FIELDS}
    }
  }
`

export const ATTACH_DIRECTORY_CLAIM_DOCUMENT = gql`
  mutation AttachDirectoryClaimDocument(
    $claimId: String!
    $document: PhotoInput!
  ) {
    attachDirectoryClaimDocument(claimId: $claimId, document: $document) {
      ${CLAIM_SELF_FIELDS}
    }
  }
`

export const SUBMIT_DIRECTORY_CLAIM = gql`
  mutation SubmitDirectoryClaim($claimId: String!) {
    submitDirectoryClaim(claimId: $claimId) {
      ${CLAIM_SELF_FIELDS}
    }
  }
`

/**
 * PUBLIC. Sends the signed one-click link to the FACILITY's own on-record
 * contact — never to the requester, and the response only ever carries a mask.
 */
export const REQUEST_DIRECTORY_REMOVAL = gql`
  mutation RequestDirectoryRemoval(
    $locationId: String!
    $channel: DirectoryClaimCodeChannel
  ) {
    requestDirectoryRemoval(locationId: $locationId, channel: $channel) {
      sentTo
      fallbackEmail
    }
  }
`

export const DIRECTORY_OPT_OUT_PREVIEW = gql`
  query DirectoryOptOutPreview($token: String!) {
    directoryOptOutPreview(token: $token) {
      locationId
      title
      address
      alreadyRemoved
    }
  }
`

export const OPT_OUT_DIRECTORY_FACILITY = gql`
  mutation OptOutDirectoryFacility($token: String!) {
    optOutDirectoryFacility(token: $token) {
      ok
      removed
    }
  }
`

/**
 * Demand evidence for the claim panel's pitch (§4.3, on-page half). PUBLIC —
 * it renders on the login wall too, where the pitch matters most.
 * `nearbySearches` is the amplified HostCTA figure (frame as recent searches
 * in the zone, never an exact count); the reveal figures are exact.
 */
export const DIRECTORY_CLAIM_DEMAND = gql`
  query DirectoryClaimDemand($locationId: String!) {
    directoryClaimDemand(locationId: $locationId) {
      nearbySearches
      nearbySearchesTier
      revealsThisMonth
      cappedAttemptsThisMonth
    }
  }
`
